import React, { useEffect, useState } from "react";
import { Loading } from "@/components/LoadingState";
import { Plus, Trash2, Edit, Users as UsersIcon, Shield, Check, Clock, Smartphone, History, LogIn, FileClock, UserCheck } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { ROLE_LABELS, ROLE_TONES, formatDate, summarizeUA, pickLabels } from "./users/userUtils";
import UserModal from "./users/UserModal";
import PermissionsModal from "./users/PermissionsModal";
import LoginHistoryModal from "./users/LoginHistoryModal";
import AuditLogModal from "./users/AuditLogModal";
import { OrphanAccountsPanel } from "./users/OrphanAccountsPanel";

export default function UsersTab() {
  const auth = useAuth();
  const [users, setUsers] = useState([]);
  const [perms, setPerms] = useState(null);
  const [personnel, setPersonnel] = useState([]);
  const [loading, setLoading] = useState(true);
  const [modalOpen, setModalOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [permModalOpen, setPermModalOpen] = useState(false);
  const [historyUser, setHistoryUser] = useState(null);
  const [auditOpen, setAuditOpen] = useState(false);
  const [linkFilter, setLinkFilter] = useState("all"); // all | linked | unlinked

  const load = async () => {
    setLoading(true);
    try {
      const [u, p, per] = await Promise.all([
        api.get("/users"),
        api.get("/role-permissions"),
        api.get("/personnel"),
      ]);
      setUsers(u.data.rows || []);
      setPerms(p.data);
      setPersonnel(per.data || []);
    } catch {
      toast.error("Kullanıcılar yüklenemedi");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const handleDelete = async (user) => {
    if (!window.confirm(`${user.email} silinsin mi? Bu işlem geri alınamaz.`)) return;
    try {
      await api.delete(`/users/${user.id}`);
      toast.success("Kullanıcı silindi");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Silinemedi");
    }
  };

  const toggleAktif = async (user) => {
    try {
      await api.put(`/users/${user.id}`, { aktif: !user.aktif });
      toast.success(user.aktif ? "Hesap pasif edildi" : "Hesap aktif edildi");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Güncellenemedi");
    }
  };

  const handleImpersonate = async (u) => {
    if (!window.confirm(`${u.email} hesabına geçiş yapmak üzeresiniz.\n\nBu modda yaptığınız tüm işlemler denetim kaydına alınır. Devam edilsin mi?`)) return;
    try {
      await auth.impersonate(u.id);
      toast.success(`${u.email} hesabına geçildi`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Impersonation başarısız");
    }
  };

  const personnelMap = Object.fromEntries(personnel.map((p) => [p.id, p]));

  const filteredUsers = users.filter((u) => {
    if (linkFilter === "linked") return !!u.personnel_id && !!personnelMap[u.personnel_id];
    if (linkFilter === "unlinked") return !u.personnel_id || !personnelMap[u.personnel_id];
    return true;
  });
  const linkedCount = users.filter((u) => u.personnel_id && personnelMap[u.personnel_id]).length;
  const unlinkedCount = users.length - linkedCount;

  return (
    <div className="space-y-4">
      <OrphanAccountsPanel onChanged={load} />
      <div className="bg-white border border-slate-200 rounded-xl shadow-sm overflow-hidden">
        <div className="p-4 md:p-5 border-b border-slate-200 flex flex-col md:flex-row md:items-center md:justify-between gap-3">
          <div>
            <h2 className="font-display text-lg md:text-xl font-bold text-slate-900 flex items-center gap-2">
              <UsersIcon size={18} /> Kullanıcı Yönetimi
            </h2>
            <p className="text-xs md:text-sm text-slate-500 mt-0.5">
              {users.length} kayıtlı kullanıcı · Rol bazlı erişim
            </p>
            <div className="mt-2 flex items-center gap-1.5 flex-wrap" data-testid="user-link-filter">
              <button
                data-testid="user-link-filter-all"
                onClick={() => setLinkFilter("all")}
                className={`px-2.5 py-1 rounded-md text-[11px] font-semibold border transition ${
                  linkFilter === "all"
                    ? "bg-slate-900 text-white border-slate-900"
                    : "bg-white text-slate-600 border-slate-200 hover:bg-slate-50"
                }`}
              >
                Tümü · {users.length}
              </button>
              <button
                data-testid="user-link-filter-linked"
                onClick={() => setLinkFilter("linked")}
                className={`px-2.5 py-1 rounded-md text-[11px] font-semibold border transition ${
                  linkFilter === "linked"
                    ? "bg-emerald-600 text-white border-emerald-600"
                    : "bg-white text-emerald-700 border-emerald-200 hover:bg-emerald-50"
                }`}
              >
                Yalnızca Bağlı · {linkedCount}
              </button>
              <button
                data-testid="user-link-filter-unlinked"
                onClick={() => setLinkFilter("unlinked")}
                className={`px-2.5 py-1 rounded-md text-[11px] font-semibold border transition ${
                  linkFilter === "unlinked"
                    ? "bg-slate-700 text-white border-slate-700"
                    : "bg-white text-slate-600 border-slate-200 hover:bg-slate-50"
                }`}
              >
                Bağlı Değil · {unlinkedCount}
              </button>
            </div>
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            <button data-testid="open-audit-log-button" onClick={() => setAuditOpen(true)} className="flex items-center gap-1.5 bg-slate-100 hover:bg-slate-200 text-slate-700 px-3 py-2 rounded-lg text-xs font-semibold">
              <FileClock size={14} /> Denetim Kaydı
            </button>
            <button data-testid="open-perms-button" onClick={() => setPermModalOpen(true)} className="flex items-center gap-1.5 bg-slate-100 hover:bg-slate-200 text-slate-700 px-3 py-2 rounded-lg text-xs font-semibold">
              <Shield size={14} /> Rol İzinleri
            </button>
            <button data-testid="add-user-button" onClick={() => { setEditing(null); setModalOpen(true); }} className="flex items-center gap-1.5 bg-slate-900 hover:bg-slate-800 text-white px-3 md:px-4 py-2 rounded-lg text-sm font-semibold">
              <Plus size={16} /> Kullanıcı Ekle
            </button>
          </div>
        </div>

        <div className="divide-y divide-slate-100" data-testid="users-list">
          {loading && <Loading text="Kullanıcılar yükleniyor..." />}
          {!loading && users.length === 0 && <div className="py-10 text-center text-slate-400 text-sm">Henüz kullanıcı yok</div>}
          {!loading && users.length > 0 && filteredUsers.length === 0 && (
            <div className="py-10 text-center text-slate-400 text-sm" data-testid="user-filter-empty">
              Bu filtreye uyan kullanıcı yok
            </div>
          )}
          {filteredUsers.map((u) => {
            const linked = u.personnel_id ? personnelMap[u.personnel_id] : null;
            const initials = (u.name || u.email).slice(0, 2).toUpperCase();
            return (
              <div key={u.id} data-testid={`user-row-${u.email}`} className="p-4 md:p-5 flex items-start gap-3">
                <div className="shrink-0 w-10 h-10 rounded-full bg-slate-900 text-white font-bold text-xs grid place-items-center">{initials}</div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="font-semibold text-slate-900 truncate">{u.name || u.email.split("@")[0]}</span>
                    <span className={`inline-flex px-2 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-wider ${ROLE_TONES[u.role]}`}>
                      {ROLE_LABELS[u.role]}
                    </span>
                    {!u.aktif && (
                      <span className="inline-flex px-2 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-wider bg-slate-200 text-slate-600">
                        Pasif
                      </span>
                    )}
                  </div>
                  <div className="text-xs text-slate-500 mt-0.5 font-mono-tab truncate">{u.email}</div>
                  <div
                    data-testid={`user-linked-personnel-${u.email}`}
                    className={`mt-1.5 inline-flex items-center gap-1.5 px-2 py-0.5 rounded-md text-[11px] font-medium border ${
                      linked
                        ? "bg-emerald-50 border-emerald-200 text-emerald-700"
                        : "bg-slate-50 border-slate-200 text-slate-400"
                    }`}
                    title="Bağlı Personel"
                  >
                    <UserCheck size={11} />
                    <span className="font-semibold">Bağlı Personel:</span>
                    {linked ? (
                      <span>
                        {linked.ad_soyad}
                        <span className="text-emerald-500/80"> · {linked.sicil_no}</span>
                      </span>
                    ) : (
                      <span>—</span>
                    )}
                  </div>
                  <div className="mt-1.5 flex items-center flex-wrap gap-x-3 gap-y-0.5 text-[11px] text-slate-500">
                    <span className="inline-flex items-center gap-1">
                      <Clock size={10} />
                      <span className="text-slate-400">Son giriş:</span>
                      <span className="font-mono-tab">{formatDate(u.last_login_at)}</span>
                    </span>
                    {u.last_user_agent && (
                      <span className="inline-flex items-center gap-1 truncate max-w-full">
                        <Smartphone size={10} />
                        <span className="truncate">{summarizeUA(u.last_user_agent)}</span>
                      </span>
                    )}
                  </div>
                </div>
                <div className="shrink-0 flex items-center gap-1">
                  {u.id !== auth.user?.id && u.aktif && (
                    <button data-testid={`impersonate-user-${u.email}`} onClick={() => handleImpersonate(u)} className="w-9 h-9 rounded-lg hover:bg-rose-50 text-rose-600 inline-flex items-center justify-center" title="Bu kullanıcı olarak giriş yap">
                      <LogIn size={14} />
                    </button>
                  )}
                  <button data-testid={`history-user-${u.email}`} onClick={() => setHistoryUser(u)} className="w-9 h-9 rounded-lg hover:bg-slate-100 text-slate-600 inline-flex items-center justify-center" title="Giriş Geçmişi">
                    <History size={14} />
                  </button>
                  <button data-testid={`toggle-aktif-${u.email}`} onClick={() => toggleAktif(u)} className={`w-9 h-9 rounded-lg inline-flex items-center justify-center ${u.aktif ? "text-emerald-600 hover:bg-emerald-50" : "text-slate-400 hover:bg-slate-100"}`} title={u.aktif ? "Pasif Et" : "Aktif Et"}>
                    <Check size={14} />
                  </button>
                  <button data-testid={`edit-user-${u.email}`} onClick={() => { setEditing(u); setModalOpen(true); }} className="w-9 h-9 rounded-lg hover:bg-slate-100 text-slate-600 inline-flex items-center justify-center" title="Düzenle">
                    <Edit size={14} />
                  </button>
                  {u.role !== "admin" && (
                    <button data-testid={`delete-user-${u.email}`} onClick={() => handleDelete(u)} className="w-9 h-9 rounded-lg hover:bg-rose-50 text-rose-500 inline-flex items-center justify-center" title="Sil">
                      <Trash2 size={14} />
                    </button>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {modalOpen && (
        <UserModal
          editing={editing}
          personnel={personnel}
          onClose={() => setModalOpen(false)}
          onSaved={() => { setModalOpen(false); load(); }}
        />
      )}

      {permModalOpen && perms && (
        <PermissionsModal
          perms={perms}
          labels={pickLabels(perms)}
          onClose={() => setPermModalOpen(false)}
          onSaved={() => { setPermModalOpen(false); load(); toast.success("Rol izinleri güncellendi · Kullanıcılar yeni girişte yeni izinleri görür"); }}
        />
      )}

      {historyUser && (
        <LoginHistoryModal user={historyUser} onClose={() => setHistoryUser(null)} />
      )}

      {auditOpen && (
        <AuditLogModal labels={pickLabels(perms)} onClose={() => setAuditOpen(false)} />
      )}
    </div>
  );
}
